import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as notificationsApi from '../../utils/notificationsApi';
import type { NotificationItem } from '../../utils/notificationsApi';

type Props = {
  unreadCount: number;
  setUnreadCount: (next: number) => void;
};

const formatTime = (ts: number) => {
  try {
    const d = new Date(ts);
    return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

const NotificationsBell: React.FC<Props> = ({ unreadCount, setUnreadCount }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'unread' | 'all'>('unread');
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const title = useMemo(() => (tab === 'unread' ? 'Unread' : 'All'), [tab]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await notificationsApi.listNotifications({ scope: tab, limit: 10 });
      setItems(resp.items || []);
    } catch (e: any) {
      setError(e?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markReadAndNavigate = async (n: NotificationItem) => {
    try {
      if (!n.is_read) {
        await notificationsApi.markNotificationRead(n.notification_id);
        setUnreadCount(Math.max(0, unreadCount - 1));
      }
    } catch {
      // best-effort; still navigate
    } finally {
      setOpen(false);
      if (n.link_url) navigate(n.link_url);
    }
  };

  const handleReadAll = async () => {
    try {
      await notificationsApi.readAllNotifications(200);
      setUnreadCount(0);
      // Refresh list
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to mark all read');
    }
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (e.target instanceof Node && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  // Load when opening or switching tabs
  useEffect(() => {
    if (!open) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative h-10 w-10 inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-gray-700 dark:text-dark-text-secondary" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-semibold flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-24px)] rounded-xl border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg-secondary shadow-lg overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-dark-border flex items-center justify-between">
            <div className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">Notifications</div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleReadAll}
                  className="text-xs font-medium text-blue-600 hover:text-blue-700"
                >
                  Mark all read
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate('/dashboard/notifications');
                }}
                className="text-xs font-medium text-gray-600 dark:text-dark-text-muted hover:text-gray-800 dark:hover:text-dark-text-primary"
              >
                See all
              </button>
            </div>
          </div>

          <div className="px-3 pt-2">
            <div className="inline-flex rounded-lg bg-gray-100 dark:bg-dark-bg-tertiary p-1">
              <button
                type="button"
                onClick={() => setTab('unread')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  tab === 'unread'
                    ? 'bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-dark-text-primary shadow-sm'
                    : 'text-gray-600 dark:text-dark-text-muted hover:text-gray-800 dark:hover:text-dark-text-primary'
                }`}
              >
                Unread
              </button>
              <button
                type="button"
                onClick={() => setTab('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  tab === 'all'
                    ? 'bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-dark-text-primary shadow-sm'
                    : 'text-gray-600 dark:text-dark-text-muted hover:text-gray-800 dark:hover:text-dark-text-primary'
                }`}
              >
                All
              </button>
            </div>
            <div className="h-2" />
          </div>

          <div className="max-h-[420px] overflow-auto">
            {loading ? (
              <div className="px-4 py-6 text-sm text-gray-600 dark:text-dark-text-muted">Loading {title.toLowerCase()}…</div>
            ) : error ? (
              <div className="px-4 py-4 text-sm text-red-700 dark:text-red-300">{error}</div>
            ) : items.length === 0 ? (
              <div className="px-4 py-6 text-sm text-gray-600 dark:text-dark-text-muted">
                No {title.toLowerCase()} notifications.
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-dark-border">
                {items.map((n) => (
                  <button
                    key={n.notification_id}
                    type="button"
                    onClick={() => markReadAndNavigate(n)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      {n.image_url ? (
                        <img
                          src={n.image_url}
                          alt=""
                          className="w-10 h-10 rounded-lg object-cover bg-gray-100 dark:bg-dark-bg-tertiary flex-shrink-0"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-dark-bg-tertiary flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate">
                            {n.title || 'Notification'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-dark-text-muted whitespace-nowrap">
                            {formatTime(n.created_at_ms)}
                          </div>
                        </div>
                        <div className="text-sm text-gray-700 dark:text-dark-text-secondary line-clamp-2">
                          {n.body}
                        </div>
                        {!n.is_read && (
                          <div className="mt-1">
                            <span className="inline-block w-2 h-2 rounded-full bg-blue-600" />
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;


